import {
    Box,
    Typography,
    useTheme,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    SelectChangeEvent,
    TextField,
    CircularProgress,
    useMediaQuery
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import moment, { Moment } from 'moment';
import analyticsService, { ActivityTimelineData } from '../../../../../services/analytics/AnalyticsService';
import { ActivityTypeColors } from '../../../../../config/constants';
import PortalCard from '../../../../../components/PortalCard';
import { TableControlBar } from '../../../../../components/TableControlBar';

export type DateRange = [Moment, Moment];

interface ActivityChartProps {
    title?: string;
    dateRange: DateRange;
}

const ActivityChart: React.FC<ActivityChartProps> = ({ title = 'User Activity Over Time', dateRange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [chartData, setChartData] = useState<ActivityTimelineData[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activityTypeFilter, setActivityTypeFilter] = useState<string>('all');
    const [anonymousIdFilter, setAnonymousIdFilter] = useState<string>('');

    const activityTypes = [
        { value: 'all', label: 'All Activities' },
        { value: 'page_view', label: 'Page Views' },
        { value: 'product_view', label: 'Product Views' },
        { value: 'cart_add', label: 'Cart Additions' },
        { value: 'checkout_start', label: 'Checkout Starts' },
        { value: 'checkout_complete', label: 'Completed Orders' },
        { value: 'sign_in', label: 'Sign Ins' },
        { value: 'sign_out', label: 'Sign Outs' },
        { value: 'sign_up', label: 'Sign Ups' },
        { value: 'navigation', label: 'Navigation' },
        { value: 'category_filtering', label: 'Category Filtering' },
    ];

    const fetchTimelineData = async () => {
        if (!dateRange[0] || !dateRange[1]) return;

        setLoading(true);
        try {
            const result = await analyticsService.getActivityTimeline(
                dateRange[0].toISOString(),
                dateRange[1].toISOString(),
                activityTypeFilter === 'all' ? undefined : activityTypeFilter,
                anonymousIdFilter || undefined
            );

            if (result.success && result.data) {
                setChartData(result.data);
            } else {
                setChartData([]);
            }
        } catch (error) {
            console.error('Error fetching timeline:', error);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimelineData();
    }, [dateRange, activityTypeFilter, anonymousIdFilter]);

    const handleActivityTypeChange = (event: SelectChangeEvent<string>) => {
        setActivityTypeFilter(event.target.value);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchTimelineData();
        setRefreshing(false);
    };

    // Calculate days difference for smart formatting
    const daysDiff = dateRange[1] && dateRange[0]
        ? dateRange[1].diff(dateRange[0], 'days')
        : 7;

    // Format labels based on date range
    const formatDateLabel = (dateStr: string) => {
        const date = moment(dateStr);
        if (daysDiff <= 1) {
            return date.format('HH:mm'); // Hourly
        } else if (daysDiff <= 31) {
            return date.format('MMM DD'); // Daily
        } else {
            return date.format('MMM YYYY'); // Monthly
        }
    };

    const xAxis = chartData.map(item => formatDateLabel(item.timePeriod));
    const activityCountValues = chartData.map(item => item.activityCount || 0);
    const pageViewValues = chartData.map(item => item.pageViews || 0);
    const productViewValues = chartData.map(item => item.productViews || 0);
    const cartAddValues = chartData.map(item => item.cartAdds || 0);
    const checkoutValues = chartData.map(item => item.checkouts || 0);
    const signInValues = chartData.map(item => item.signIns || 0);
    const signOutValues = chartData.map(item => item.signOuts || 0);
    const signUpValues = chartData.map(item => item.signUps || 0);
    const navigationValues = chartData.map(item => item.navigation || 0);
    const categoryFilteringValues = chartData.map(item => item.categoryFiltering || 0);

    // Use centralized colors from constants
    const activityCountColor = '#BB86FC'; // Special color for total (not in ActivityTypeColors)
    

    return (
        <PortalCard
            sx={{
                p: isMobile ? 2 : 1.5,
                pb: isMobile ? 4 : 1.5,
                mb: isMobile ? 2 : 0,
                height: isMobile ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
            }}
        >
            {/* Title Section */}
            <Box sx={{ mb: isMobile ? 2 : 1 }}>
                <Typography variant={isMobile ? 'h6' : 'subtitle1'} fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.25 }}>
                    {title}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: isMobile ? undefined : '0.75rem' }}>
                    All user interactions and behaviors
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: isMobile ? 380 : 150, width: '100%', position: 'relative', overflow: 'visible' }}>
                {loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : chartData.length > 0 ? (
                    <LineChart
                        xAxis={[{ 
                            data: xAxis, 
                            scaleType: 'point',
                            tickLabelStyle: {
                                fontSize: isMobile ? 10 : 12,
                            }
                        }]}
                        series={[
                            {
                                data: activityCountValues,
                                label: isMobile ? 'Total' : 'Total Activities',
                                color: activityCountColor,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                                area: true,
                            },
                            {
                                data: pageViewValues,
                                label: isMobile ? 'Pages' : 'Page Views',
                                color: ActivityTypeColors.page_view,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: productViewValues,
                                label: isMobile ? 'Products' : 'Product Views',
                                color: ActivityTypeColors.product_view,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: cartAddValues,
                                label: isMobile ? 'Cart' : 'Cart Additions',
                                color: ActivityTypeColors.cart_add,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: checkoutValues,
                                label: isMobile ? 'Checkout' : 'Checkout Starts',
                                color: ActivityTypeColors.checkout_start,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: signInValues,
                                label: isMobile ? 'In' : 'Sign Ins',
                                color: ActivityTypeColors.sign_in,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: signOutValues,
                                label: isMobile ? 'Out' : 'Sign Out',
                                color: ActivityTypeColors.sign_out,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: signUpValues,
                                label: isMobile ? 'Up' : 'Sign Ups',
                                color: ActivityTypeColors.sign_up,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: navigationValues,
                                label: isMobile ? 'Nav' : 'Navigation',
                                color: ActivityTypeColors.navigation,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                            {
                                data: categoryFilteringValues,
                                label: isMobile ? 'Filter' : 'Category Filtering',
                                color: ActivityTypeColors.category_filtering,
                                curve: 'monotoneX',
                                showMark: !isMobile,
                            },
                        ]}
                        height={isMobile ? 380 : isTablet ? 240 : 200}
                        margin={{ 
                            top: 5, 
                            right: isMobile ? 15 : 20, 
                            bottom: isMobile ? 180 : 55, 
                            left: isMobile ? 30 : 35 
                        }}
                        slotProps={{
                            legend: {
                                position: {
                                    vertical: 'bottom',
                                    horizontal: 'middle'
                                },
                                padding: { bottom: isMobile ? 5 : 0, top: isMobile ? 15 : 8 },
                                itemGap: isMobile ? 10 : 12,
                                markGap: isMobile ? 4 : 4,
                                itemMarkWidth: isMobile ? 10 : 8,
                                itemMarkHeight: isMobile ? 10 : 8,
                                labelStyle: {
                                    fontSize: isMobile ? 11 : 11,
                                }
                            },
                        }}
                        sx={{
                            '.MuiLineElement-root': {
                                strokeWidth: isMobile ? 2 : 4,
                                opacity: 0.9
                            },
                            '.MuiMarkElement-root': {
                                strokeWidth: isMobile ? 1 : 2,
                                r: isMobile ? 3 : 6,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    strokeWidth: 3,
                                    r: 8
                                }
                            },
                            '.MuiAreaElement-root': {
                                fillOpacity: 0.1
                            },
                            '& .MuiChartsAxis-tickLabel': {
                                fontWeight: '500',
                                fontSize: isMobile ? '0.7rem' : '0.875rem',
                                fill: theme.palette.text.secondary
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                                strokeWidth: 1
                            },
                            '& .MuiChartsAxis-tick': {
                                stroke: theme.palette.divider
                            },
                            '& .MuiChartsLegend-root': {
                                paddingTop: isMobile ? 4 : 8,
                                flexDirection: 'row',
                                '& .MuiChartsLegend-label': {
                                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                                    fontWeight: '500',
                                    fill: theme.palette.text.primary
                                },
                                '& .MuiChartsLegend-mark': {
                                    rx: 2
                                }
                            }
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='text.secondary'>
                            No activity data available for the selected period
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default ActivityChart;

